<template>
  <!-- one fill-in slot of a search sentence: type to search, pick one (or up
       to field.max for chip fields). Suggestions show as soon as it's clicked. -->
  <div
    class="sf"
    :class="{ 'sf-open': isOpen, 'sf-filled': hasValue }"
    @click="focusInput"
  >
    <template v-if="field.multiple">
      <span v-for="(item, index) in modelValue" :key="item.id" class="sf-chip">
        {{ item.name }}
        <button
          type="button"
          class="sf-chip-x"
          aria-label="Remove"
          @mousedown.prevent
          @click.stop="removeAt(index)"
        >&times;</button>
      </span>
    </template>
    <span v-else-if="modelValue" class="sf-chip">
      {{ modelValue.name }}
      <button
        type="button"
        class="sf-chip-x"
        aria-label="Clear"
        @mousedown.prevent
        @click.stop="clearSingle"
      >&times;</button>
    </span>

    <input
      v-if="canType"
      ref="input"
      v-model="query"
      class="sf-input"
      type="text"
      autocomplete="off"
      :placeholder="placeholder"
      :size="inputSize"
      @focus="openMenu"
      @blur="isOpen = false"
      @input="onInput"
      @keydown.down.prevent="move(1)"
      @keydown.up.prevent="move(-1)"
      @keydown.enter.prevent="pickActive"
      @keydown.esc="onEsc"
      @keydown.backspace="onBackspace"
    />

    <span v-if="field.multiple && modelValue.length" class="sf-count">
      {{ modelValue.length }}/{{ field.max }}
    </span>

    <ul v-if="isOpen && canType" class="sf-menu" role="listbox">
      <li class="sf-menu-head">
        {{ field.multiple ? "Select up to " + field.max : "Suggestions" }}
      </li>
      <li
        v-for="(option, index) in matches"
        :key="option.id"
        class="sf-option"
        :class="{ active: index === activeIndex }"
        role="option"
        @mousedown.prevent="pick(option)"
        @mouseenter="activeIndex = index"
      >
        <i class="fa fa-search sf-option-icon"></i>
        <span>
          {{ splitMatch(option.name).before }}<strong>{{ splitMatch(option.name).match }}</strong>{{ splitMatch(option.name).after }}
        </span>
      </li>
      <li v-if="isLoading && !matches.length" class="sf-empty">Searching&hellip;</li>
      <li v-else-if="!matches.length" class="sf-empty">
        {{ query ? 'No matches for "' + query + '"' : "No suggestions" }}
      </li>
    </ul>
  </div>
</template>
<script>
const SEARCH_DELAY = 250; // ms of typing pause before asking for suggestions

export default {
  name: "SentenceSlot",
  props: {
    field: { type: Object, required: true },
    // (term) => Promise<[{ id, name }]>
    search: { type: Function, required: true },
    // { id, name } (or null) for single fields, an array of them for chip fields
    modelValue: { type: [Object, Array], default: null },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      query: "",
      isOpen: false,
      isLoading: false,
      results: [],
      activeIndex: 0,
      timer: null,
      // only the newest request may fill the list
      requestId: 0,
    };
  },
  computed: {
    hasValue() {
      return this.field.multiple ? this.modelValue.length > 0 : !!this.modelValue;
    },
    canType() {
      return this.field.multiple
        ? this.modelValue.length < this.field.max
        : !this.modelValue;
    },
    placeholder() {
      return this.field.multiple && this.modelValue.length
        ? this.field.addLabel
        : this.field.label;
    },
    // the input grows with its text so the sentence keeps reading naturally
    inputSize() {
      return Math.max((this.query || this.placeholder).length, 4) + 1;
    },
    matches() {
      const taken = this.field.multiple ? this.modelValue.map((item) => item.id) : [];
      return this.results.filter((option) => !taken.includes(option.id));
    },
  },
  beforeUnmount() {
    clearTimeout(this.timer);
  },
  methods: {
    focusInput() {
      if (this.$refs.input) {
        this.$refs.input.focus();
      }
    },
    openMenu() {
      this.isOpen = true;
      this.fetch();
    },
    onInput() {
      this.isOpen = true;
      this.activeIndex = 0;
      clearTimeout(this.timer);
      this.timer = setTimeout(this.fetch, SEARCH_DELAY);
    },
    fetch() {
      const id = ++this.requestId;
      this.isLoading = true;
      this.search(this.query.trim())
        .then((results) => {
          if (id === this.requestId) {
            this.results = results;
            this.activeIndex = 0;
          }
        })
        .catch(() => {
          if (id === this.requestId) {
            this.results = [];
          }
        })
        .finally(() => {
          if (id === this.requestId) {
            this.isLoading = false;
          }
        });
    },
    move(step) {
      this.isOpen = true;
      const count = this.matches.length;
      if (count) {
        this.activeIndex = (this.activeIndex + step + count) % count;
      }
    },
    pickActive() {
      if (this.isOpen && this.matches[this.activeIndex]) {
        this.pick(this.matches[this.activeIndex]);
      }
    },
    pick(option) {
      const hadQuery = !!this.query;
      this.query = "";
      this.activeIndex = 0;
      if (this.field.multiple) {
        this.$emit("update:modelValue", [...this.modelValue, option]);
        // keep the menu up for the next chip while there is room for one
        this.isOpen = this.modelValue.length + 1 < this.field.max;
        if (this.isOpen && hadQuery) {
          this.fetch();
        }
      } else {
        this.$emit("update:modelValue", option);
        this.isOpen = false;
      }
    },
    removeAt(index) {
      this.$emit(
        "update:modelValue",
        this.modelValue.filter((_, i) => i !== index)
      );
    },
    clearSingle() {
      this.$emit("update:modelValue", null);
      this.$nextTick(this.focusInput);
    },
    // an open menu swallows Esc, so only a second press closes the popup
    onEsc(event) {
      if (this.isOpen) {
        event.stopPropagation();
        this.isOpen = false;
      }
    },
    onBackspace() {
      if (this.field.multiple && !this.query && this.modelValue.length) {
        this.removeAt(this.modelValue.length - 1);
      }
    },
    splitMatch(name) {
      const term = this.query.trim();
      const at = term ? name.toLowerCase().indexOf(term.toLowerCase()) : -1;
      if (at < 0) {
        return { before: name, match: "", after: "" };
      }
      return {
        before: name.slice(0, at),
        match: name.slice(at, at + term.length),
        after: name.slice(at + term.length),
      };
    },
  },
};
</script>
<style scoped>
.sf {
  position: relative;
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-height: 42px;
  max-width: 100%;
  padding: 4px 10px;
  background: #fff;
  border: 1.5px dashed #c3cad5;
  border-radius: 8px;
  cursor: text;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
}

.sf:hover {
  border-color: #98a2b3;
}

.sf-open,
.sf-open:hover {
  border-style: solid;
  border-color: #de4a38;
  box-shadow: 0 0 0 3px rgba(222, 74, 56, 0.12);
  z-index: 5;
}

.sf-filled:not(.sf-open) {
  border-style: solid;
  border-color: #e4e7ec;
  background: #fafbfc;
}

.sf-input {
  min-width: 0;
  max-width: 100%;
  padding: 2px 0;
  border: 0;
  outline: 0;
  background: transparent;
  font-family: "Calibri Regular", "Segoe UI", system-ui, sans-serif;
  font-size: 17px;
  color: #101828;
}

.sf-input::placeholder {
  color: #98a2b3;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.sf-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px 4px 11px;
  background: #fdecea;
  border: 1px solid #f6c5be;
  border-radius: 999px;
  font-family: "Calibri Bold", "Segoe UI", system-ui, sans-serif;
  font-size: 15px;
  line-height: 1.2;
  color: #b4321f;
  white-space: nowrap;
}

.sf-chip-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  font-size: 15px;
  line-height: 1;
  color: #b4321f;
  cursor: pointer;
}

.sf-chip-x:hover {
  background: rgba(180, 50, 31, 0.14);
}

.sf-count {
  font-family: "Calibri Regular", "Segoe UI", system-ui, sans-serif;
  font-size: 12px;
  color: #98a2b3;
}

/* ---- suggestions ---- */
.sf-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: -1px;
  z-index: 10;
  min-width: 260px;
  max-width: 80vw;
  max-height: 290px;
  margin: 0;
  padding: 6px;
  overflow-y: auto;
  list-style: none;
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  box-shadow: 0 12px 28px rgba(16, 24, 40, 0.16);
}

.sf-menu-head {
  padding: 4px 10px 6px;
  font-family: "Calibri Bold", "Segoe UI", system-ui, sans-serif;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #98a2b3;
}

.sf-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 6px;
  font-family: "Calibri Regular", "Segoe UI", system-ui, sans-serif;
  font-size: 15px;
  color: #344054;
  cursor: pointer;
}

.sf-option strong {
  color: #101828;
}

.sf-option.active {
  background: #f2f4f7;
}

.sf-option-icon {
  font-size: 12px;
  color: #98a2b3;
}

.sf-empty {
  padding: 10px;
  font-family: "Calibri Regular", "Segoe UI", system-ui, sans-serif;
  font-size: 14px;
  color: #667085;
}
</style>
